/**
 * Country Intelligence Index (CII) — composite risk score for ~20 priority countries.
 *
 * 4 components, each scored 0-25:
 * - Unrest: protest and social disorder events
 * - Conflict: conflict and military events
 * - Security: cyber incidents, sanctions, infrastructure events
 * - Information: news volume velocity (event count in 24h window)
 *
 * Overall CII = sum of 4 components (0-100).
 * Trend: delta > 10 = rising, < -10 = falling, else stable.
 * Classification: >= 70 critical, >= 55 high, >= 40 elevated, >= 25 normal, < 25 low.
 */

import type { NormalizedEvent } from '@/sources/base-source';
import type { CIIScore } from './types';
import { getEventTimestamp } from './types';

const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;

/** Priority countries for CII tracking. */
const CII_COUNTRIES: { code: string; name: string }[] = [
  { code: 'US', name: 'United States' },
  { code: 'CN', name: 'China' },
  { code: 'RU', name: 'Russia' },
  { code: 'UA', name: 'Ukraine' },
  { code: 'IL', name: 'Israel' },
  { code: 'IR', name: 'Iran' },
  { code: 'KP', name: 'North Korea' },
  { code: 'TW', name: 'Taiwan' },
  { code: 'IN', name: 'India' },
  { code: 'PK', name: 'Pakistan' },
  { code: 'SA', name: 'Saudi Arabia' },
  { code: 'YE', name: 'Yemen' },
  { code: 'SY', name: 'Syria' },
  { code: 'MM', name: 'Myanmar' },
  { code: 'AF', name: 'Afghanistan' },
  { code: 'VE', name: 'Venezuela' },
  { code: 'NG', name: 'Nigeria' },
  { code: 'ET', name: 'Ethiopia' },
  { code: 'SD', name: 'Sudan' },
  { code: 'CD', name: 'DR Congo' },
];

/** Severity weights for component scoring. */
const SEVERITY_WEIGHT: Record<string, number> = {
  critical: 5,
  high: 3,
  medium: 2,
  low: 1,
  info: 0.5,
};

function severityWeight(sev?: string): number {
  return SEVERITY_WEIGHT[sev ?? 'info'] ?? 0.5;
}

/** Filter events matching a country code or name (case-insensitive). */
function filterByCountry(
  events: NormalizedEvent[],
  code: string,
  name: string,
): NormalizedEvent[] {
  const upperCode = code.toUpperCase();
  const upperName = name.toUpperCase();
  return events.filter((e) => {
    const cc = e.location?.countryCode?.toUpperCase();
    if (cc === upperCode) return true;
    const cn = e.location?.country?.toUpperCase();
    if (cn === upperName) return true;
    return false;
  });
}

/** Unrest component (0-25): protest and social disorder events weighted by severity. */
function calcUnrest(countryEvents: NormalizedEvent[]): number {
  let score = 0;
  for (const ev of countryEvents) {
    if (ev.type === 'protest' || ev.type === 'social') {
      score += severityWeight(ev.severity);
    }
  }
  return Math.min(25, Math.round(score));
}

/** Conflict component (0-25): conflict and military events weighted by severity. */
function calcConflict(countryEvents: NormalizedEvent[]): number {
  let score = 0;
  for (const ev of countryEvents) {
    if (ev.type === 'conflict' || ev.type === 'military') {
      score += severityWeight(ev.severity) * 1.2;
    }
  }
  return Math.min(25, Math.round(score));
}

/** Security component (0-25): cyber incidents, sanctions, infrastructure events. */
function calcSecurity(countryEvents: NormalizedEvent[]): number {
  let score = 0;
  for (const ev of countryEvents) {
    if (
      ev.type === 'cyber_incident' ||
      ev.type === 'sanction' ||
      ev.type === 'infrastructure'
    ) {
      score += severityWeight(ev.severity);
    }
  }
  return Math.min(25, Math.round(score));
}

/**
 * Information component (0-25): news and information volume in the 24h window.
 * Uses absolute volume thresholds — higher volume = more activity = higher risk signal.
 */
function calcInformation(countryEvents: NormalizedEvent[]): number {
  const infoTypes = new Set([
    'political', 'diplomatic', 'social', 'economic', 'custom',
  ]);

  let count = 0;
  for (const ev of countryEvents) {
    if (infoTypes.has(ev.type)) {
      count++;
    } else if (ev.source === 'rss' || ev.source === 'gdelt') {
      // RSS and GDELT events carry information signal
      count++;
    }
  }

  if (count >= 30) return 25;
  if (count >= 20) return 20;
  if (count >= 12) return 15;
  if (count >= 6) return 10;
  if (count >= 3) return 5;
  return Math.min(3, count);
}

/**
 * Calculate CII for a single country.
 */
export function calculateCII(
  events: NormalizedEvent[],
  countryCode: string,
  countryName: string,
  previousScore?: CIIScore,
): CIIScore {
  const cutoff = Date.now() - TWENTY_FOUR_HOURS_MS;
  const recent = events.filter(
    (e) => getEventTimestamp(e) >= cutoff,
  );
  const countryEvents = filterByCountry(recent, countryCode, countryName);

  const components = {
    unrest: calcUnrest(countryEvents),
    conflict: calcConflict(countryEvents),
    security: calcSecurity(countryEvents),
    information: calcInformation(countryEvents),
  };

  const overall =
    components.unrest +
    components.conflict +
    components.security +
    components.information;

  // Trend: compare to previous score if available
  let trend: CIIScore['trend'] = 'stable';
  if (previousScore) {
    const delta = overall - previousScore.overall;
    if (delta > 10) trend = 'rising';
    else if (delta < -10) trend = 'falling';
  }

  return {
    countryCode,
    countryName,
    overall,
    components,
    trend,
    lastUpdated: new Date().toISOString(),
  };
}

/**
 * Calculate CII for all 20 priority countries.
 */
export function calculateAllCII(
  events: NormalizedEvent[],
  previousScores?: Map<string, CIIScore>,
): Map<string, CIIScore> {
  const scores = new Map<string, CIIScore>();

  for (const { code, name } of CII_COUNTRIES) {
    const previous = previousScores?.get(code);
    scores.set(code, calculateCII(events, code, name, previous));
  }

  return scores;
}

/** Get the list of CII priority country codes. */
export function getCIICountryCodes(): string[] {
  return CII_COUNTRIES.map((c) => c.code);
}

/** Get CII classification label from score. */
export function getCIIClassification(
  score: number,
): 'critical' | 'high' | 'elevated' | 'normal' | 'low' {
  if (score >= 70) return 'critical';
  if (score >= 55) return 'high';
  if (score >= 40) return 'elevated';
  if (score >= 25) return 'normal';
  return 'low';
}

/** Get hex color for CII score. */
export function getCIIColor(score: number): string {
  const cls = getCIIClassification(score);
  switch (cls) {
    case 'critical': return '#ef4444';
    case 'high': return '#f59e0b';
    case 'elevated': return '#eab308';
    case 'normal': return '#22c55e';
    case 'low': return '#64748b';
  }
}
